<template>
  <div class="d-flex flex-column flex-1">
    <div class="position-relative">
      <div class="hero-stage">
        <video 
          ref="heroVideo"
          class="width-100 d-block"
          :src="heroVideoUrl"
          muted
          playsinline
          autoplay
          loop
          preload="none"
        ></video>
      </div>
      <!-- Tear -->
      <div class="position-absolute d-flex align-center" style="bottom: -10px; left: 0; z-index: 2;">
        <img class="width-100 d-block d-md-none" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Fheader%2Fhome-tear-mobile.png?alt=media&token=d452a10c-657e-4e6f-9629-c655f2291931" alt="">
        <img class="width-100 d-none d-md-block" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Fheader%2Fhome-tear.png?alt=media&token=d6ebcf0b-226a-4c86-bd40-5e00610194af" alt="">
      </div>
    </div>
    
    <div class="renedian-brown--background jost-bold text-uppercase renedian-sand--text d-flex text-center align-center justify-center py-4 font-size-huge">
      Where will your adventure begin?
    </div>

    <!-- Regions Section -->
    <div class="d-flex flex-column renedian-sand--background justify-center align-center py-12" v-if="!loadingRegions && regions.length">
      <!-- Region Title -->
      <div class="text-uppercase barlow-regular renedian-dark-sand--text mb-4 font-size-lg">Select a region</div>
      <!-- Region Icons -->
      <div class="d-flex flex-column flex-md-row align-center justify-center">
        <div 
          v-for="region in regions" 
          :key="region.id"
          class="region-icon my-4 mx-md-4 position-relative"
          @click="navigateToRegion(region)"
        >
          <img
            v-if="region.iconLarge" 
            :src="$img(region.iconLarge, 400)" 
            :alt="region.name"
            class="d-none d-md-flex"
            style="height: 12vw"
          >
          <img 
            v-if="region.iconSmall" 
            :src="$img(region.iconSmall, 400)" 
            :alt="region.name"
            class="d-flex d-md-none"
            style="height: 7vw"
          >
          <!-- Private Tour Badge -->
          <div v-if="region.privateTour" class="private-tour-badge">
            <v-icon small color="white">mdi-lock</v-icon>
            <span class="d-none d-md-inline">Private</span>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex renedian-light-sand--background">
      <img class="width-100" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Fheader%2Fsand-tear.png?alt=media&token=965de0a8-4bcf-4a39-9638-e7c9ea6cb155" alt="" />
    </div>

    <!-- Featured Tours -->
    <div class="py-12 d-flex flex-column align-center justify-center renedian-light-sand--background">
      <div class="d-flex jost-bold renedian-dark-red--text text-uppercase text-center font-size-xl">Featured Tour</div>
      <!-- Custom Carousel -->
      <div class="custom-carousel-container position-relative mt-12">
        <!-- Carousel Content -->
        <div class="carousel-slide-container" style="min-height: 300px;">
          <div 
            v-for="(tour, index) in featuredTours"
            :key="tour.id || tour.slug || `tour-${index}`"
            :class="{ 'active': currentSlide === index }"
            class="carousel-slide d-flex flex-column"
          >
            <!-- Desktop version -->
            <div class="d-none d-md-flex flex-row pb-12" style="width: 80vw; border-bottom: 1px solid #5a6660">
              <div class="d-flex flex-column align-self-start">
                <img :src="$img(tour.images.badge, 400)" :alt="`${tour.name} badge`" loading="lazy" style="width: 15vw; height: auto; max-width: 200px;">
              </div>
              <div class="d-flex flex-column flex-1 px-6">
                <div class="barlow-condensed-bold text-uppercase renedian-sand-green--text font-size-huge">{{ tour.name }}</div>
                <div class="barlow-condensed-regular renedian-orange--text text-uppercase font-size-lg">{{ tour.duration }} days</div>
                <div class="barlow-regular mt-4 font-size-xs">
                  {{ tour.content.website.detailedDescription }}
                </div>
              </div>
              <div class="d-flex flex-column" style="width: 15vw; height: auto; max-width: 200px;">
                <div class="mb-4 position-relative">
                  <img src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FPrice%20Tag%20-%20with%20text.svg?alt=media&token=f5ebe485-2318-4942-af7f-683aa8de78ad" alt="" />
                    <div class="position-absolute white--text font-size-md" style="bottom: 1vw; right: 1vw; font-weight: 600;">{{ tour.pricing.tour.base | price }}</div>
                  </div>
                  <div class="explore-button" @click="navigateToTour(tour)">
                    <img src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FEXPLORE%20button.svg?alt=media&token=1f03be4f-4232-4e1b-b377-565d52f3336c" alt="" />
                  </div>
              </div>
            </div>

            <!-- Maps & Details -->
            <div class="d-flex flex-column flex-md-row" style="width: 80vw;">
              <div class="flex-1">
                <img :src="$img(tour.images.map, 800)" :alt="`${tour.name} tour map`" loading="lazy" style="width: 100%; height: auto;">
              </div>
              <div class="flex-1 d-flex flex-column">
                <div class="position-relative d-flex flex-column align-center pa-12">
                  <img class="width-80 lifted-photo" :src="$img(tour.images.thumbnail, 800)" :alt="tour.name" loading="lazy" />
                </div>
                <div class="d-flex flex-column align-center">
                  <img v-if="tour.location.countries.length == 1" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F1_Countries_icon.svg?alt=media&token=cd97fe6a-522c-4654-ad0b-7a1f41a1dff3" alt="" />
                  <img v-if="tour.location.countries.length == 2" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F2_Countries_icon.svg?alt=media&token=a74853e4-9c83-4313-a267-4ee17f20a16c" alt="" />
                  <img v-if="tour.location.countries.length == 3" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F3_Countries_icon.svg?alt=media&token=31dd47bd-d8a6-4b0b-b61e-1cd9e24d9eec" alt="" />
                  <img v-if="tour.location.countries.length == 4" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F4_Countries_icon.svg?alt=media&token=2373571c-56a7-44bc-92c6-15f13d00f46d" alt="" />
                  <img v-if="tour.location.countries.length == 5" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F5_Countries_icon.svg?alt=media&token=8ab626fc-2a02-491d-984a-4f57ebf699cd" alt="" />
                  <img v-if="tour.location.countries.length == 6" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F6_Countries_icon.svg?alt=media&token=26df114e-a352-4eeb-8461-a9fa71fbdc2b" alt="" />
                  <img v-if="tour.location.countries.length == 7" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F7_Countries_icon.svg?alt=media&token=9c2bf405-f341-4f4d-b0ff-230d8adac88b" alt="" />
                  <img v-if="tour.location.countries.length == 8" style="width: 3vw; min-width: 40px;" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F8_Countries_icon.svg?alt=media&token=ea75db82-e6aa-4d37-b795-c97645624c88" alt="" />
                  <div class="barlow-condensed-bold-italic text-uppercase mt-4 renedian-dark-red--text font-size-sm">Countries visited on this tour:</div>
                  <div class="barlow-condensed-regular-italic renedian-dark-red--text px-12 font-size-sm">{{ tour.location.countries.join(', ') }}</div>
                </div>
              </div>
            </div>
            
            <!-- Mobile version -->
            <div class="d-flex d-md-none flex-column align-center justify-center pa-4 width-90-vw">
              <div class="d-flex flex-column align-center text-center">
                <div class="barlow-condensed-bold text-uppercase renedian-sand-green--text mb-2 font-size-huge" style="border-bottom: 3px solid #5a6660">
                  {{ tour.name }}
                </div>
                <div class="barlow-condensed-regular renedian-burnt-orange--text text-uppercase font-size-lg">
                  {{ tour.duration }} days  /  {{ tour.location.countries.length }} {{ tour.location.countries.length > 1 ? 'countries' : 'country' }}
                </div>
                <div class="barlow-regular mt-4 font-size-xs">
                  {{ tour.content.website.detailedDescription }}
                </div>
              </div>
            </div>

            <!-- Riding Bar -->
            <div class="d-flex flex-column align-center py-12">
              <div class="d-none d-md-flex width-60-vw justify-center">
                <RidingBar :riding="tour.riding" :duration="tour.duration" />
              </div>
              <div class="d-flex d-md-none width-90-vw justify-center">
                <RidingBar :riding="tour.riding" :duration="tour.duration" />
              </div>
            </div>

          </div>
        </div>
         
        <!-- Dot Navigation -->
        <div v-if="featuredTours.length > 1" class="carousel-dots d-flex justify-center mt-4">
          <div
            v-for="(tour, index) in featuredTours"
            :key="`dot-${tour.id || index}`"
            :class="{ 'active': currentSlide === index }"
            class="carousel-dot"
            @click="goToSlide(index)"
            :data-index="index"
            :title="`Go to slide ${index + 1}`"
          ></div>
        </div>
      </div>
    </div>

    <!-- <img src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FSwipe%20Finger%20-%20Icon.svg?alt=media&token=2779cdbe-6d9e-4a0b-be81-e0160d5ee593" /> -->

    <!-- Choosing Renedian -->
    <div class="width-100 renedian-light-sand--background">
      <img class="d-none d-md-block width-100" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Fheader%2Forange-tear.png?alt=media&token=c2233aaf-3205-4674-b9ac-19364dfac33c" alt="" />
      <img class="d-block d-md-none width-100" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Fheader%2Forange-tear-mobile.png?alt=media&token=be806ff5-c3df-4674-b943-1f80c6c6a96d" alt="" />
    </div>
    <div class="d-flex flex-column renedian-orange--background text-center align-center py-12">
      
      <!-- <div class="d-flex "> -->
      <!-- <div>Choosing Renedian for your adventure</div> -->
        <!--  text-center py-4 -->
      <div class="d-flex jost-bold text-uppercase white--text font-size-lg justify-center width-90-vw">Choosing Renedian for your adventure</div>
      <!-- </div> -->

      <div class="d-flex flex-column flex-md-row py-12 align-self-center width-80">
        <div class="d-flex flex-1 flex-row mb-12 mb-md-0">
          <img style="width: 18px" class="mr-4" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FSpear%20icon.svg?alt=media&token=45742728-4e09-427a-8fa1-56ee04b065f5" alt="" />
          <div class="d-flex flex-column">
            <div class="d-flex barlow-condensed-bold text-uppercase renedian-dark-red--text text-left font-size-xl">Established.</div>
            <div class="text-left renedian-sand--text barlow-regular font-size-xs">
              Renedian founder Rene Cormier has been crafting our itineraries for 15 years, always striving to improve on that perfect blend of riding, adventure, activities, and comfort.
            </div>            
          </div>
        </div>
        <div class="d-flex flex-1 flex-row mb-12 mb-md-0 mx-md-12">
          <img style="width: 18px" class="mr-4" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FSpear%20icon.svg?alt=media&token=45742728-4e09-427a-8fa1-56ee04b065f5" alt="" />
          <div class="d-flex flex-column">
            <div class="d-flex barlow-condensed-bold text-uppercase renedian-dark-red--text text-left font-size-xl">Committed.</div>
            <div class="text-left renedian-sand--text barlow-regular font-size-xs">
              We create all of our own tours. Along with our global staff, we are 100% committed to ensuring that every day while journeying with us, your tour meets your high expectations.
            </div>            
          </div>
        </div>
        <div class="d-flex flex-1 flex-row mb-0">
          <img style="width: 18px" class="mr-4" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FSpear%20icon.svg?alt=media&token=45742728-4e09-427a-8fa1-56ee04b065f5" alt="" />
          <div class="d-flex flex-column">
            <div class="d-flex barlow-condensed-bold text-uppercase renedian-dark-red--text text-left font-size-xl">Trusted.</div>
            <div class="text-left renedian-sand--text barlow-regular font-size-xs">
              Every year more than half our riders are repeat guests. Our tours consistently provide great bikes, unique hotel choices and a mature blend of riding and off-the-bike experiences.
            </div>            
          </div>
        </div>
      </div>

      <div class="d-flex barlow-condensed-bold text-uppercase justify-center renedian-dark-red--text font-size-md">100 percent guide owned and operated</div>
    </div>

    <!-- BMW Partner -->
    <div class="width-100 d-flex flex-column align-center py-12">
      <img class="d-none d-md-block width-30" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FBMW%20Partner%20Text%20%26%20Logo.svg?alt=media&token=9647bdcc-9879-4b1c-9479-b95f560684c9" alt="BMW Motorrad partner logo" />
      <img class="d-block d-md-none width-50" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FBMW%20Partner%20Text%20%26%20Logo.svg?alt=media&token=9647bdcc-9879-4b1c-9479-b95f560684c9" alt="BMW Motorrad partner logo" />
      <img class="d-none d-md-block width-100 my-12" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FBMW%20Partner%20Photo.jpg?alt=media&token=4a95af20-297a-4f86-b77a-9020eafceeab', 1600)" alt="Renedian riders with BMW motorcycles" loading="lazy" />
      <img class="d-block d-md-none width-100 my-12" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FBMW%20Partner%20Photo%20-%20Mobile.jpg?alt=media&token=ea30c304-1e1e-47fb-a3cb-b5ada2d9a62f', 800)" alt="Renedian riders with BMW motorcycles" loading="lazy" />
      <div class="d-flex barlow-regular grey--text text--darken-3 text-uppercase font-size-sm">We are official partners of BMW Motorrad</div>
    </div>

    <!-- World Class -->
    <div class="d-flex flex-column renedian-light-sand--background align-center py-12">
      <div class="d-flex jost-bold renedian-dark-red--text text-uppercase text-center font-size-huge">World Class Support</div>
      <div style="width: 80vw;" class="d-flex flex-column flex-md-row mt-12">

        <div class="flex-1 d-flex flex-column align-center grey--text text--darken-3 pb-12">
          <img style="width: 50px" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FOur%20Team%20-%20Icon.svg?alt=media&token=588cccbb-fc87-49b4-a47d-9324acbdea6e" alt="" />
          <div class="d-flex jost-semibold text-uppercase text-center font-size-lg">Our Team</div>
          <p class="barlow-regular text--darken-3 text-left mt-8 font-size-xs">
            Our Renedian team members are skilled, knowledgeable, well trained (and some of the friendliest on this planet) to support and guide you on every one of our journeys. We strive to keep you safe, happy, and informed and always aim to navigate the challenges of group dynamics and everything else that riding in a "wild" place can bring. Be confident that booking with Renedian, knowing "who will lead the way"!
          </p>
          <!-- Avatar Cycling -->
          <div class="avatar-explorer mt-8">
            <div class="avatar-strip flex-wrap" ref="avatarStrip">
              <div
                v-for="(person, index) in team"
                :key="index"
                class="avatar-item"
                @click="go(person.profile)"
              >
                <div class="avatar-circle">
                  <img :src="$img(person.photo, 400)" :alt="person.name" loading="lazy">
                </div>
                <div class="avatar-overlay">
                  <div class="barlow-condensed-bold text-uppercase renedian-dark-red--text">{{ person.name }}</div>
                  <div class="barlow-regular renedian-sand-green--text">{{ person.title }}</div>
                  <div v-if="person.location" class="barlow-regular-italic grey--text text--darken-2">{{ person.location }}</div>
                </div>
              </div>
            </div>
          </div>
          <img style="width: 20%" class="mt-12" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FWaiving%20Piet.png?alt=media&token=d1990a38-a910-456b-bf61-d38c5128da52', 600)" alt="Piet waving" loading="lazy">
          <v-btn @click="go('MeetTheGuides')" class="mt-6" color="orange darken-3" dark elevation="0">Meet the Crew</v-btn>
        </div>
        <div class="flex-1 width-100 d-flex flex-column align-center mb-12 mb-md-0">
          <img style="width: 60%" class="lifted-photo" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FPiet%20%26%20Rene.jpg?alt=media&token=727e04c0-5c84-4ae2-a15b-68659166fd3e', 800)" alt="Piet and Rene, Renedian tour guides" loading="lazy" />
          <img class="width-30 mt-12" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FOn-Road%20Tour%20Guide%20-%20Icon.svg?alt=media&token=d5ecce75-7397-49b9-a707-13bfbab24f82" alt="" />
        </div>
      </div>
      
      <!-- Newest Member of the Team -->
      <div class="d-none d-md-flex flex-row renedian-blue-background width-100 mt-12">
        <div class="d-flex flex-column flex-grow-1 align-center justify-center pa-6">
          <div class="white--text text-uppercase jost-bold font-size-md text-center">Renedian Custom Support Truck</div>
          <div class="jost-semibold text-uppercase font-size-sm" style="color: #efab47;">Available in Africa</div>
          <div class="jost-bold text-uppercase pt-4 font-size-sm" style="color: #e9deb9;">Custom built for safety, comfort, and convenience</div>
          <div class="white--text barlow-bold text-center pt-2 font-size-xs">Invite a non-riding friend to join your trip and let them ride in the truck</div>
          <div class="black py-2 px-4 text-uppercase jost-semibold mt-4 cursor-pointer" style="color: #eda73d" @click="openTruckPdfDialog">Learn More</div>
        </div>
        <div>
          <img class="d-block cursor-pointer" style="margin-top: -70px" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Ftruck-banner-600.png?alt=media&token=f55165c7-c07f-4cdf-a814-049416df9176', 800)" alt="Renedian custom support truck" loading="lazy" @click="openTruckPdfDialog" />
        </div>
      </div>
      <!-- Newest Member of the Team (mobile) -->
      <div class="d-flex d-md-none flex-column align-center">
        <div class="d-flex flex-column align-center justify-center text-center py-10 pa-6 renedian-blue--background width-100">
          <div class="white--text text-uppercase jost-bold font-size-md d-flex text-center">Renedian Custom Support Truck</div>
          <div class="jost-semibold text-uppercase font-size-sm" style="color: #efab47;">Available in Africa</div>
          <div class="jost-bold text-uppercase pt-4 font-size-sm" style="color: #e9deb9;">Custom built for safety, comfort, and convenience</div>
          <div class="white--text barlow-bold text-center pt-2 font-size-xs">Invite a non-riding friend to join your trip and let them ride in the truck</div>
          <div class="black py-2 px-4 text-uppercase jost-semibold mt-4 cursor-pointer" style="color: #eda73d" @click="openTruckPdfDialog">Learn More</div>
        </div>
        <div class="width-100-vw">
          <img class="width-100" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Ftruck-mobile.png?alt=media&token=802c9989-76cd-4c87-834b-16fd5b3e3e78', 800)" alt="Renedian custom support truck" loading="lazy" />
        </div>
      </div>

      <!-- Rider Stories and Local Lore -->
      <div class="d-flex flex-column flex-md-row align-center mt-12 width-80-vw">
        <div @click="go('VoicesOfExperience')" class="flex-1 width-100 mb-6 mb-md-0 mr-md-1 cursor-pointer">
          <div class="d-flex flex-column align-center overflow-hidden" style="border-radius: 30px; background: #544c44">
            <div class="d-flex jost-bold renedian-sand--text text-uppercase text-center font-size-xl my-2">Rider Stories</div>
            <img class="width-100 d-block" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FRider%20Stories%20-%20Image.jpg?alt=media&token=11684e5a-14d9-4d22-8426-22fd2c409253', 800)" alt="Rider stories" loading="lazy" />
          </div>
        </div>
        <div @click="go('LocalLore')" class="flex-1 width-100 ml-md-1 cursor-pointer">
          <div class="d-flex flex-column align-center overflow-hidden" style="border-radius: 30px; background: #79665c">
            <div class="d-flex jost-bold renedian-sand--text text-uppercase text-center font-size-xl my-2">Local Lore</div>
            <img class="width-100 d-block" :src="$img('https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FLocal%20Lore%20-%20Image.jpg?alt=media&token=234ba341-9032-483b-be38-e3c31f100317', 800)" alt="Local lore" loading="lazy" />
          </div>
        </div>
      </div>
    </div>

    <!-- Support Truck PDF Dialog -->
    <v-dialog v-model="truckPdfDialog" max-width="80%" @click:outside="truckPdfDialog = false">
      <v-card>
        <v-card-title class="d-flex justify-end pa-0">
          <v-btn icon @click="truckPdfDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0 pdf-viewer-container" style="height: 85vh; overflow: auto; background: #525252;">
          <object
            :data="truckPdfUrl + '#navpanes=0&toolbar=1&zoom=50'"
            type="application/pdf"
            style="width: 100%; height: 100%; border: none; display: block;"
          >
            <p>Your browser does not support PDFs. <a :href="truckPdfUrl" target="_blank">Download the PDF</a>.</p>
          </object>
        </v-card-text>
      </v-card>
    </v-dialog>

    <div class="d-flex flex-column renedian-light-sand--background align-center pb-12">
      <div class="d-md-flex barlow-regular-italic renedian-sand--background pa-12 grey--text text--darken-3 font-size-xs width-80-vw" style="border-radius: 15px;">
        Our Adventure Motorcycle Tours are known for visiting off the beaten track and exciting destinations, while staying in upscale accommodations and catering to all dietary needs. Great adventure by day, and great comfort by night!
        <br></br>
        We will take you to the ends of the Earth, from the top of North America to the southernmost city in South America, from big game safaris in Africa to Mongolia's remote Gobi Desert, our adventure motorcycle tours will leave you with unforgettable memories.
      </div>
    </div>

    <!-- Footer Offset -->
    <div class="d-flex flex-column renedian-light-sand--background align-center py-12"></div>
    
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { PUBLIC_CACHE_SCHEMA_VERSION, fetchPublicCache } from '@/constants/publicCache'
import { localCacheGet, localCacheSet } from '@/utils/localCache'
import HeroicMap from '@/blocks/HeroicMap.vue'
import TopTours from '@/blocks/TopTours.vue'
import FeaturedTours from '@/blocks/FeaturedTours.vue'
import AboutRenedian from '@/blocks/AboutRenedian.vue'
import ChoosingRenedian from '@/blocks/ChoosingRenedian.vue'
import Reviews from '@/blocks/Reviews.vue'
import RiderTales from '@/blocks/RiderTales.vue'
import Statistics from '@/blocks/Statistics.vue'
import RidingBar from '@/components/RidingBar.vue'
import { emitPrerenderReady, isPrerenderBuild } from '@/utils/prerender'
import { writePrerenderState, readPrerenderState } from '@/utils/prerenderState'

export default {
  name: 'Landing',
  props: ['popup'],
  components: {
    HeroicMap,
    TopTours,
    FeaturedTours,
    AboutRenedian,
    ChoosingRenedian,
    Reviews,
    RiderTales,
    Statistics,
    RidingBar
  },
  data() {
    // Seed from the prerendered state blob so the client's first render matches
    // the prerendered DOM (no blank-then-fill of regions / featured tours).
    const seed = readPrerenderState()
    const seededRegions = Array.isArray(seed.regions) ? seed.regions : []
    const seededFeaturedTours = Array.isArray(seed.featuredTours) ? seed.featuredTours : []
    return {
    heroVideoUrl: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fhero%2Flanding_video_one.mp4?alt=media&token=f6a50e72-d292-40b2-8d75-b6fd915b800d',
    // Regions data
    regions: seededRegions,
    loadingRegions: seededRegions.length === 0,
    // Featured tours data
    featuredTours: seededFeaturedTours,
    loadingFeaturedTours: seededFeaturedTours.length === 0,
    // Employees (Our Team)
    employees: [],
    employeesLoading: true,
    activeEmployeeId: null,
    stripMouseX: null,
    // Carousel data
    currentSlide: 0,
    employeesCacheTtlMs: 6 * 60 * 60 * 1000,
    // Support Truck PDF Dialog
    truckPdfDialog: false,
    truckPdfUrl: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FSupport%20Truck%20-%20Learn%20More%20Pop-Up%20Page.pdf?alt=media&token=af569c98-a8cc-4436-97a3-1f21dcad9498',

    // Team
    team: [
      {
        name: 'Colette Cormier',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FColette%20C.png?alt=media&token=809ba839-1109-40d4-88e3-413308ddf75f',
        title: 'Administration',
        location: 'Canada',
        profile: '/choosing-renedian/meet-the-crew'
      },
      {
        name: 'Danise Zuidmeer',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FDanise%20Z.png?alt=media&token=2834a261-c383-4fa3-b0e3-2628f98228b7',
        title: 'Administration',
        location: 'South Africa',
        profile: '/choosing-renedian/meet-the-crew/danise-zuidmeer'
      },
      // {
      //   name: 'David C.',
      //   photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FDavid%20C.png?alt=media&token=77007e88-fb19-45da-aa5a-a2f60b5b88f1',
      // },
      // {
      //   name: 'Edwardo V.',
      //   photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FEdwardo%20V.png?alt=media&token=e6923637-3ea3-4807-b7fa-e54ec6b0f116',
      // },
      // {
      //   name: 'Emiliano A.',
      //   photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FEmiliano%20A.png?alt=media&token=7307ff46-320e-48b0-a881-a05370bdb892',
      // },
      
      {
        name: 'Liz Jansen',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FLiz%20J.png?alt=media&token=957311c1-0c09-4eec-8369-6359342be183',
        title: 'Alumni Manager',
        location: 'Canada',
        profile: '/choosing-renedian/meet-the-crew'
      },
      {
        name: 'Jana Morkel',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/springbok%2Fimages%2F1759153147597-WhatsApp%20Image%202025-09-29%20at%2013.10.02.jpeg?alt=media&token=6004f546-c9ee-4ce3-a782-e45ec086b44f',
        title: 'Administration',
        location: 'South Africa',
        profile: '/choosing-renedian/meet-the-crew'
      },
      {
        name: 'Aaron Farr',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FAaron%20F.png?alt=media&token=5a28e726-edce-4ca2-a563-9f36da8e3687',
        title: 'Information Technology',
        location: 'Canada',
        profile: '/choosing-renedian/meet-the-crew'
      },
      {
        name: 'Gary Davidson',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FGary%20D.png?alt=media&token=ac57397b-2e69-4fea-9b6d-64ecf4cc20af',
        title: 'Brand Manager',
        location: 'Canada',
        profile: '/choosing-renedian/meet-the-crew'
      },
      {
        name: 'Luke Krajcarksi',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FLuke%20K.png?alt=media&token=b101f1d8-3f6f-4454-94ec-ceebbf420fb8',
        title: 'Manager',
        location: 'Canada',
        profile: '/choosing-renedian/meet-the-crew'
      },
      {
        name: 'Wes Boehlke',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FWes%20B.png?alt=media&token=eda71298-8ca9-4bf9-a4a2-d5d8d2430cc8',
        title: 'Crew',
        location: 'Canada',
        profile: '/choosing-renedian/meet-the-crew'
      },
      // {
      //   name: 'Yuto O.',
      //   photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FYuto%20O.png?alt=media&token=b0e6f092-a0eb-4d88-a544-4862b95f48f3',
      // },
      {
        name: 'Horea Marinescu',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FHorea.png?alt=media&token=df74f26e-35ae-493e-a07e-68202a99c923',
        title: 'Guide',
        location: 'South Africa',
        profile: '/choosing-renedian/meet-the-crew'
      },
      {
        name: 'Jaime Saiz Cuena',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FJaime%20C.png?alt=media&token=b63729d2-e017-4a5a-8809-fc9e159c1ea1',
        title: 'Guide',
        location: 'Chile',
        profile: '/choosing-renedian/meet-the-crew'
      },
      // {
      //   name: 'John Wellburn',
      //   photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FJohn%20W.png?alt=media&token=0a917e49-162b-4287-9ebf-4510633a744a',
      // },
      {
        name: 'Mike Higgs',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FMike%20H.png?alt=media&token=0674ebde-2b15-4520-9ae3-a70a9afb128a',
        title: 'Guide',
        location: 'South Africa',
        profile: '/choosing-renedian/meet-the-crew/mike-higgs'
      },
      {
        name: 'Rob House',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FRob%20H.png?alt=media&token=56a24d34-bebd-4a15-9d23-09c5b5179d04',
        title: 'Guide',
        location: 'Scotland',
        profile: '/choosing-renedian/meet-the-crew/rob-house'
      },
      {
        name: 'Piet Human',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FPiet%20H.png?alt=media&token=c53ec93c-7bbf-40e5-b936-8d2536f5f64b',
        title: 'Director',
        location: 'United States',
        profile: '/choosing-renedian/meet-the-crew/piet-human'
      },
      {
        name: 'Rene Cormier',
        photo: 'https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2Fteam%2FRene%20C.png?alt=media&token=20acc2b1-03e7-4bea-8354-39e129f4735c',
        title: 'Director',
        location: 'Canada',
        profile: '/choosing-renedian/meet-the-crew/rene-cormier'
      },
    ],
    }
  },
  async created() {
    const loaders = [
      this.loadRegions(),
      this.loadFeaturedTours(),
    ]
    if (!isPrerenderBuild()) {
      loaders.push(this.loadEmployees())
    }
    await Promise.all(loaders)
    if (isPrerenderBuild()) {
      // Inline the data this page rendered with so the client can seed the same
      // initial state and avoid re-rendering the regions / featured tours.
      writePrerenderState({ regions: this.regions, featuredTours: this.featuredTours })
    }
    this.$nextTick(() => emitPrerenderReady())
  },
  methods: {
    // Load regions from public cache (Storage CDN)
    async loadRegions() {
      try {
        const cacheData = await fetchPublicCache('regions')
        if (cacheData?.data && Array.isArray(cacheData.data)) {
          this.regions = cacheData.data
          this.loadingRegions = false
          return
        }
        console.warn('Public cache for regions missing data, falling back to direct query')
        const db = getFirestore()
        const regionsRef = collection(db, 'regions')
        const snapshot = await getDocs(regionsRef)
        const regions = []
        snapshot.forEach((doc) => {
          const region = doc.data()
          region.id = doc.id
          regions.push(region)
        })
        this.regions = regions.sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        console.warn('Public cache for regions fetch failed, falling back to direct query', error)
        try {
          const db = getFirestore()
          const regionsRef = collection(db, 'regions')
          const snapshot = await getDocs(regionsRef)
          const regions = []
          snapshot.forEach((doc) => {
            const region = doc.data()
            region.id = doc.id
            regions.push(region)
          })
          this.regions = regions.sort((a, b) => a.name.localeCompare(b.name))
        } catch (inner) {
          console.error('Error loading regions:', inner)
        }
      } finally {
        this.loadingRegions = false
      }
    },
    
    // Load featured tours from public cache (Storage CDN)
    async loadFeaturedTours() {
      try {
        const cacheData = await fetchPublicCache('templates')
        if (cacheData?.data && Array.isArray(cacheData.data)) {
          const featuredTours = cacheData.data
            .filter(tour => tour.featured === true)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          this.featuredTours = featuredTours
          this.loadingFeaturedTours = false
          return
        }
        console.warn('Public cache for templates missing data, falling back to direct query')
        const db = getFirestore()
        const templatesRef = collection(db, 'templates')
        const featuredQuery = query(templatesRef, where('featured', '==', true))
        const snapshot = await getDocs(featuredQuery)
        const featuredTours = []
        snapshot.forEach((doc) => {
          const tour = doc.data()
          tour.id = doc.id
          const templateType = tour.type || 'open'
          if (templateType !== 'private') {
            featuredTours.push(tour)
          }
        })
        this.featuredTours = featuredTours.sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        console.warn('Public cache for templates fetch failed, falling back to direct query', error)
        try {
          const db = getFirestore()
          const templatesRef = collection(db, 'templates')
          const featuredQuery = query(templatesRef, where('featured', '==', true))
          const snapshot = await getDocs(featuredQuery)
          const featuredTours = []
          snapshot.forEach((doc) => {
            const tour = doc.data()
            tour.id = doc.id
            const templateType = tour.type || 'open'
            if (templateType !== 'private') {
              featuredTours.push(tour)
            }
          })
          this.featuredTours = featuredTours.sort((a, b) => a.name.localeCompare(b.name))
        } catch (inner) {
          console.error('Error loading featured tours:', inner)
        }
      } finally {
        this.loadingFeaturedTours = false
      }
    },
    
    // Load employees (active only) with cache-first SWR
    async loadEmployees() {
      const cacheKey = 'landing_employees_v1'
      const cached = localCacheGet(cacheKey, {
        ttlMs: this.employeesCacheTtlMs,
        version: PUBLIC_CACHE_SCHEMA_VERSION,
      })
      if (cached && Array.isArray(cached)) {
        this.employees = cached
        this.employeesLoading = false
        return
      }

      try {
        const db = getFirestore()
        const employeesRef = collection(db, 'employees')
        const employeesQuery = query(employeesRef, where('active', '==', true))
        const snapshot = await getDocs(employeesQuery)
        const employees = []
        snapshot.forEach((doc) => {
          const e = doc.data()
          // The agents are on staff but not on the public team (2026-10-29).
          if (e.kind === 'agent') return
          const locale = e.location && e.location.locale ? e.location.locale : ''
          const country = e.location && e.location.country ? e.location.country : ''
          const loc = [locale, country].filter(Boolean).join(', ')
          employees.push({
            id: doc.id,
            name: e.name || 'Unnamed',
            title: e.title || '',
            photo: e.photo || '',
            location: loc,
            order: typeof e.order === 'number' ? e.order : 0
          })
        })
        const sorted = employees.sort((a, b) => {
          const ao = (a.order ?? 0)
          const bo = (b.order ?? 0)
          if (ao !== bo) return ao - bo
          return a.name.localeCompare(b.name)
        })
        this.employees = sorted
        localCacheSet(cacheKey, sorted, { version: PUBLIC_CACHE_SCHEMA_VERSION })
      } catch (error) {
        console.error('Error loading employees:', error)
      } finally {
        this.employeesLoading = false
      }
    },

    // Avatar interactions
    toggleEmployee(id) {
      this.activeEmployeeId = this.activeEmployeeId === id ? null : id
    },
    hoverEmployee(id) {
      this.activeEmployeeId = id
    },
    getInitials(name) {
      if (!name || typeof name !== 'string') return '??'
      const parts = name.trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + last).toUpperCase() || name.slice(0, 2).toUpperCase()
    },
    // No neighbor scaling; only active gets scaled via CSS
    
    // Navigate to region page
    navigateToRegion(region) {
      if (region.slug) {
        this.go('Region', { region: region.slug })
      }
    },
    
    // Navigate to tour page
    navigateToTour(tour) {
      const regionSlug = tour.location?.region?.slug
      const tourSlug = tour.reference?.slug
      if (regionSlug && tourSlug) {
        this.$router.push(`/adventure-motorcycle-tours/${regionSlug}/${tourSlug}`)
      }
    },
    
    // Carousel navigation
    goToSlide(index) {
      console.log('Clicked dot:', index, 'Current slide:', this.currentSlide);
      this.currentSlide = index;
    },
    
    // Open Support Truck PDF Dialog
    openTruckPdfDialog() {
      this.truckPdfDialog = true;
    }
  },
  filters: {
    price(value) {
      return '$' + value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  }
}
</script>

<style scoped>
.hero-stage {
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 16 / 9;
}

.hero-stage video {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.cursor-pointer {
  cursor: pointer;
}

.region-icon:hover {
  opacity: 0.5;
  cursor: pointer;
}

.private-tour-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #9c27b0;
  color: white;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

@media (max-width: 960px) {
  .private-tour-badge {
    top: -6px;
    right: -6px;
    padding: 3px 6px;
    font-size: 0.65rem;
  }
}

.ma-4:hover h3 {
  color: #1976d2;
  transition: color 0.3s ease;
}

/* Custom Carousel Styles */
.custom-carousel-container {
  width: 100%;
}

.carousel-slide-container {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-slide {
  position: absolute;
  width: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-slide.active {
  opacity: 1;
  position: relative;
}

/* Dot Navigation */
.carousel-dots {
  margin-top: 20px;
  position: relative;
  z-index: 5;
  pointer-events: auto;
}

.carousel-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #ded4b7;
  margin: 0 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  pointer-events: auto;
  flex-shrink: 0;
}

.carousel-dot.active {
  background-color: #5a6660;
  transform: scale(1.2);
}

.carousel-dot:hover {
  transform: scale(1.1);
  opacity: 0.8;
}

.explore-button img {
  transition: opacity 0.2s ease-in-out;
}
.explore-button img:hover {
  cursor: pointer;
  opacity: 0.5;
}

/* Avatar Explorer */
.avatar-explorer { width: 100%; }
.avatar-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: wrap;
}
.avatar-item {
  position: relative;
  margin-left: -18px;
  transition: transform 0.2s ease, filter 0.2s ease, opacity 0.2s ease;
  z-index: 1; /* base layer so items stack predictably */
}
.avatar-item:first-child { margin-left: 0; }
.avatar-item.dimmed { filter: none; opacity: 1; }
.avatar-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 6px 14px rgba(0,0,0,0.18);
  background: #eae3cf;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.avatar-item:hover .avatar-circle,
.avatar-item.active .avatar-circle {
  transform: scale(1.25);
  box-shadow: 0 14px 26px rgba(0,0,0,0.24);
  background: #ffffff;
  cursor: pointer;
}
.avatar-item:hover,
.avatar-item.active {
  z-index: 100; /* ensure active/hovered avatar renders above its neighbors */
}
.avatar-circle img { width: 100%; height: 100%; object-fit: cover; display: block; opacity: 1; mix-blend-mode: normal; }
.avatar-initials { font-weight: 700; color: #5a6660; }
.avatar-overlay {
  position: absolute;
  bottom: calc(100% + 14px);
  left: 50%;
  transform: translateX(-50%) scale(0.96);
  background: rgba(255,255,255,0.98);
  border-radius: 10px;
  padding: 6px 10px;
  width: max-content;
  max-width: 220px;
  box-shadow: 0 12px 22px rgba(0,0,0,0.18);
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease, transform 120ms ease;
  text-align: center;
  z-index: 3;
}
.avatar-item:hover .avatar-overlay,
.avatar-item.active .avatar-overlay {
  opacity: 1;
  transform: translateX(-50%) scale(1);
}
.avatar-item.active {
  opacity: 1 !important;
}
@media (max-width: 960px) {
  .avatar-circle { width: 56px; height: 56px; }
  .avatar-item { margin-left: -14px; }
}

.pdf-viewer-container {
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x pan-y pinch-zoom;
}

.pdf-viewer-container object {
  touch-action: pan-x pan-y pinch-zoom;
}
</style>
